import React from 'react';
import Footer from '../components/common/Footer';
import SiteHeader from '../components/common/SiteHeader';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  ServerIcon,
  GlobeAltIcon,
  ClockIcon,
  EnvelopeIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const Disclaimer: React.FC = () => {
  const tableOfContents = [
    { id: '1-introduction', title: 'Introduction', icon: DocumentTextIcon },
    { id: '2-no-investment-advice', title: 'No Investment Advice', icon: ExclamationTriangleIcon },
    { id: '3-risk-disclosure', title: 'Risk Disclosure', icon: ShieldCheckIcon },
    { id: '4-accuracy-of-information', title: 'Accuracy of Information', icon: InformationCircleIcon },
    { id: '5-third-party-content', title: 'Third-Party Content', icon: GlobeAltIcon },
    { id: '6-regulatory-compliance', title: 'Regulatory Compliance', icon: ShieldCheckIcon },
    { id: '7-limitation-of-liability', title: 'Limitation of Liability', icon: ExclamationTriangleIcon },
    { id: '8-jurisdiction', title: 'Jurisdiction', icon: GlobeAltIcon },
    { id: '9-updates-to-disclaimer', title: 'Updates to Disclaimer', icon: DocumentTextIcon },
    { id: '10-contact-us', title: 'Contact Us', icon: EnvelopeIcon }
  ];

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <SiteHeader />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 py-16">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Disclaimer
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              TWENTY FOUR CAPITAL LLC
            </motion.p>
            <motion.div 
              className="mt-4 text-white/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p>Effective Date: {currentDate}</p>
              <p>Last Updated: {currentDate}</p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-32 bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <motion.a
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors py-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(item.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                          window.history.pushState(null, '', `#${item.id}`);
                        }
                      }}
                    >
                      <item.icon className="h-4 w-4 text-gray-400" />
                      <span>{item.title}</span>
                    </motion.a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm p-8 space-y-12">
                {/* Section 1: Introduction */}
                <section id="1-introduction">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">1.</span> Introduction
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      This Disclaimer ("Disclaimer") applies to all information, services, and content provided by Twenty Four Capital LLC ("Company," "we," "us," or "our") through our website, mobile applications, technology platform, and any other communication channels.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by this Disclaimer. If you do not agree with any part of this Disclaimer, you should not use our services.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      This Disclaimer should be read in conjunction with our Privacy Policy, Terms of Service, and other applicable legal documents.
                    </p>
                  </div>
                </section>

                {/* Section 2: No Investment Advice */}
                <section id="2-no-investment-advice">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">2.</span> No Investment Advice
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-red-800 mb-3">Important Notice</h3>
                      <p className="text-red-700">
                        The information provided through our services is for informational and educational purposes only and does not constitute investment advice, financial advice, trading advice, or any other sort of advice.
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-4">
                      We are not a registered investment advisor, broker-dealer, or financial advisor. Our services are designed to provide technology-enabled equity financing solutions and educational content, not to provide personalized investment recommendations.
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Any decisions you make regarding investments, financing, or other financial matters should be based on your own research and consultation with qualified professionals, including:
                    </p>
                    
                    <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                      <li>Licensed financial advisors</li>
                      <li>Certified public accountants</li>
                      <li>Securities attorneys</li>
                      <li>Tax professionals</li>
                      <li>Other qualified financial professionals</li>
                    </ul>
                  </div>
                </section>

                {/* Section 3: Risk Disclosure */}
                <section id="3-risk-disclosure">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">3.</span> Risk Disclosure
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Investment Risks</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        All investments carry inherent risks, including but not limited to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Market volatility and fluctuations</li>
                        <li>Economic downturns and recessions</li>
                        <li>Company-specific risks and failures</li>
                        <li>Regulatory changes and compliance risks</li>
                        <li>Liquidity risks and inability to sell</li>
                        <li>Currency and exchange rate risks</li>
                        <li>Interest rate and inflation risks</li>
                        <li>Political and geopolitical risks</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Equity Financing Risks</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Equity financing through our platform involves additional risks:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Dilution of existing shareholders</li>
                        <li>Market price impact of share issuances</li>
                        <li>Exchange cap limitations and compliance</li>
                        <li>Baby shelf restrictions for certain companies</li>
                        <li>Regulatory approval requirements</li>
                        <li>Market timing and execution risks</li>
                        <li>Counterparty and settlement risks</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Technology Risks</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Our technology platform is subject to various risks:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>System failures and technical issues</li>
                        <li>Cybersecurity threats and data breaches</li>
                        <li>Internet connectivity and availability</li>
                        <li>Software bugs and algorithmic errors</li>
                        <li>Third-party service provider risks</li>
                        <li>Data accuracy and reliability</li>
                      </ul>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-orange-800 mb-3">Risk Warning</h3>
                      <p className="text-orange-700">
                        Past performance does not guarantee future results. The value of investments can go down as well as up, and you may lose some or all of your invested capital. You should carefully consider whether investing is suitable for you in light of your financial circumstances and risk tolerance.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 4: Accuracy of Information */}
                <section id="4-accuracy-of-information">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">4.</span> Accuracy of Information
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Information Sources</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We strive to provide accurate and up-to-date information, but we cannot guarantee the accuracy, completeness, or timeliness of any information provided through our services. Information may be derived from:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Public sources and regulatory filings</li>
                        <li>Third-party data providers</li>
                        <li>Company-provided information</li>
                        <li>Market data and analytics</li>
                        <li>Our proprietary algorithms and models</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 No Warranty</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        All information is provided "as is" without warranty of any kind, either express or implied, including but not limited to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Warranties of merchantability</li>
                        <li>Warranties of fitness for a particular purpose</li>
                        <li>Warranties of non-infringement</li>
                        <li>Warranties of accuracy or completeness</li>
                        <li>Warranties of uninterrupted or error-free operation</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Information Updates</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Information may become outdated or inaccurate over time. We do not undertake to update or revise any information to reflect new information or future events. Users should independently verify all information before making any decisions.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 5: Third-Party Content */}
                <section id="5-third-party-content">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">5.</span> Third-Party Content
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 External Links</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Our services may contain links to third-party websites, applications, or content. We do not:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Endorse or approve third-party content</li>
                        <li>Control third-party websites or their content</li>
                        <li>Guarantee the accuracy of third-party information</li>
                        <li>Accept responsibility for third-party content</li>
                        <li>Monitor third-party websites for changes</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Third-Party Services</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We may integrate with or use third-party services for:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Data enrichment and verification</li>
                        <li>Market data and analytics</li>
                        <li>Payment processing and settlement</li>
                        <li>Communication and notifications</li>
                        <li>Security and compliance services</li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        Use of third-party services is subject to their respective terms of service and privacy policies.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.3 User Responsibility</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Users are responsible for evaluating the suitability, accuracy, and completeness of any third-party content or services. We encourage users to review third-party terms and policies before engaging with their services.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 6: Regulatory Compliance */}
                <section id="6-regulatory-compliance">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">6.</span> Regulatory Compliance
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Securities Laws</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Our services are subject to various securities laws and regulations, including:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Securities Act of 1933</li>
                        <li>Securities Exchange Act of 1934</li>
                        <li>Regulation D and other SEC regulations</li>
                        <li>State securities laws (Blue Sky laws)</li>
                        <li>FINRA rules and regulations</li>
                        <li>Exchange listing requirements</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Compliance Requirements</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Users must comply with applicable laws and regulations:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Accredited investor verification</li>
                        <li>Anti-money laundering requirements</li>
                        <li>Know-your-customer procedures</li>
                        <li>Reporting and disclosure obligations</li>
                        <li>Tax compliance requirements</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Regulatory Changes</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Securities laws and regulations are subject to change. We may modify our services to comply with new requirements, but users remain responsible for their own compliance with applicable laws.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 7: Limitation of Liability */}
                <section id="7-limitation-of-liability">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">7.</span> Limitation of Liability
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-red-800 mb-3">Limitation of Liability</h3>
                      <p className="text-red-700">
                        To the maximum extent permitted by applicable law, Twenty Four Capital LLC shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages, including but not limited to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-red-700 ml-4 mt-3">
                        <li>Loss of profits or revenue</li>
                        <li>Loss of business opportunities</li>
                        <li>Loss of data or information</li>
                        <li>Investment losses or damages</li>
                        <li>Reputational harm</li>
                        <li>Any other damages or losses</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Maximum Liability</h3>
                      <p className="text-gray-700 leading-relaxed">
                        In no event shall our total liability exceed the amount paid by you for our services in the twelve (12) months preceding the claim, or $100, whichever is greater.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">7.2 Essential Purpose</h3>
                      <p className="text-gray-700 leading-relaxed">
                        The limitations of liability in this section are fundamental elements of the basis of the bargain between you and us. Some jurisdictions do not allow the limitation of liability, so these limitations may not apply to you.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 8: Jurisdiction */}
                <section id="8-jurisdiction">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">8.</span> Jurisdiction
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 Governing Law</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        This Disclaimer and your use of our services shall be governed by and construed in accordance with the laws of the State of Wyoming, United States, without regard to conflict of law principles.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">8.2 Dispute Resolution</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Any disputes arising from this Disclaimer or our services shall be resolved through:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Good faith negotiations</li>
                        <li>Mediation (if required by law)</li>
                        <li>Binding arbitration in Wyoming</li>
                        <li>Litigation in Wyoming courts (if arbitration fails)</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">8.3 International Users</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Our services are intended for use within the United States. International users are responsible for compliance with local laws and regulations. We do not guarantee that our services are appropriate or available for use in other jurisdictions.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 9: Updates to Disclaimer */}
                <section id="9-updates-to-disclaimer">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">9.</span> Updates to Disclaimer
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may update this Disclaimer from time to time to reflect changes in our services, legal requirements, or other factors. We will notify users of material changes by posting the updated Disclaimer on our website with a new effective date. Continued use of our services after such changes constitutes acceptance of the updated Disclaimer.
                  </p>
                </section>

                {/* Section 10: Contact Us */}
                <section id="10-contact-us">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">10.</span> Contact Us
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you have questions about this Disclaimer or our services, contact us:
                  </p>
                  
                  <div className="bg-orange-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Twenty Four Capital LLC</h3>
                    <p className="text-gray-700 mb-1">1309 Coffeen Ave Ste 17816</p>
                    <p className="text-gray-700 mb-3">Sheridan, WY 82801</p>
                    <p className="text-gray-700 mb-1">Email: legal@24lp.io</p>
                    <p className="text-gray-700">Website: https://24lp.io</p>
                  </div>

                  <p className="text-gray-700 leading-relaxed">
                    For legal matters or regulatory inquiries, we recommend consulting with qualified legal counsel familiar with securities law and your specific circumstances.
                  </p>
                </section>
              </div>
            </main>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Disclaimer; 